import React from 'react';
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import type { EventGuest } from '../../types/eventGuest';
import { EVENT_GUEST_RELATIONSHIP_LABELS } from '../../types/eventGuest';

interface GuestPickerRowProps {
  guests: EventGuest[];
  selectedGuestIds: string[];
  onToggle: (guestId: string) => void;
  /** Remaining event capacity AFTER subtracting current selections. */
  remainingCapacity: number;
  /** Family-member count already selected — included in the capacity sum. */
  selectedFamilyMemberCount: number;
}

/**
 * Plan 4.7 — Inline guest picker for the EventDetailPage register flow.
 * Mirrors the family-member picker shape: a column of FormControlLabel
 * checkboxes. Redacted guests are filtered out defensively (the caller
 * should already exclude them).
 */
const GuestPickerRow: React.FC<GuestPickerRowProps> = ({
  guests,
  selectedGuestIds,
  onToggle,
  remainingCapacity,
  selectedFamilyMemberCount,
}) => {
  const visibleGuests = guests.filter((g) => !g.isRedacted);
  const totalSelected = selectedFamilyMemberCount + selectedGuestIds.length;
  const capacityReached = totalSelected >= remainingCapacity;

  if (visibleGuests.length === 0) return null;

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        Guests
      </Typography>
      {visibleGuests.map((guest) => {
        const checked = selectedGuestIds.includes(guest.id);
        const label =
          guest.relationship === 'Other' && guest.relationshipOther
            ? guest.relationshipOther
            : EVENT_GUEST_RELATIONSHIP_LABELS[guest.relationship];

        return (
          <FormControlLabel
            key={guest.id}
            control={
              <Checkbox
                checked={checked}
                onChange={() => onToggle(guest.id)}
                // Disable unchecked rows when adding them would exceed
                // capacity. Checked rows stay enabled so users can toggle off.
                disabled={!checked && capacityReached}
              />
            }
            label={`${guest.firstName} ${guest.lastName} (${label})`}
            sx={{ display: 'block' }}
          />
        );
      })}
    </Box>
  );
};

export default GuestPickerRow;
